import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useTwilioAppState } from '../state/';
import useDominantSpeaker from './useDominantSpeaker';
import useVideoContext from './useVideoContext';
import { Logger } from '../utils';
import * as types from "../../../../constants/actionTypes";

export default function useParticipants() {
    const { room } = useVideoContext();
    const dominantSpeaker = useDominantSpeaker();
    const [participants, setParticipants] = useState(room && room.participants && room.participants.values() ? Array.from(room.participants.values()) : []);
    const [showWaitingMsg, setShowWaitingMsg] = useState(false);
    const _participants = useSelector((state) => state.videoVisit?.participants) || [];
    const dispatch = useDispatch();
    const { _isMounted } = useTwilioAppState();
    // When the dominant speaker changes, they are moved to the front of the participants array.
    // This means that the most recent dominant speakers will always be near the top of the
    // ParticipantStrip component.
    useEffect(() => {
        if (dominantSpeaker && _isMounted && _isMounted.current === true) {
            setParticipants(prevParticipants => [
                dominantSpeaker,
                ...prevParticipants.filter(participant => participant !== dominantSpeaker),
            ]);
        }
    }, [dominantSpeaker, _isMounted]);

    useEffect(() => {
        const updatedParticipantList = room && room.participants && room.participants.values() ? Array.from(room.participants.values()) : [];
        if (_isMounted && _isMounted.current === true) {
            setParticipants(updatedParticipantList);
            setShowWaitingMsg(updatedParticipantList.length === 0 ? true : false);
        }

        const participantConnected = participant => {
            Logger("UI.Twilio.Participant.Connected", new Date(), { participant });
            const idParts = participant.identity.split('||');
            const localParticipantType = room?.localParticipant?.identity.split[2];
            if ((localParticipantType == 'Provider' && idParts[2] == 'Patient') || (localParticipantType == 'Patient' && idParts[2] == 'Provider')) {
                _participants.push({ roomId: room.name, participantId: idParts[1], connectedTime: new Date() });
            }
            dispatch({ type: types.SET_PARTICIPANTS, participants: _participants });
            setParticipants(prevParticipants => [...prevParticipants, participant]);
            setShowWaitingMsg(false);
        };

        const participantDisconnected = participant => {
            const idParts = participant?.identity?.split('||');
            const participantId = idParts.length > 1 && idParts[1];
            let index = _participants.findIndex(p => p.roomId == room.name && p.participantId == participantId);
            if (index > -1) {
                const diffMs = (new Date() - new Date(_participants[index].connectedTime)); // milliseconds between the times
                const diffMins = (((diffMs % 86400000) % 3600000) / 60000).toFixed(2); // minutes
                Logger("UI.Twilio.Participant.DisConnected", new Date(), { participant }, room.name, diffMins);
                _participants.splice(index, 1);
                dispatch({ type: types.SET_PARTICIPANTS, participants: _participants });
            }
            else {
                Logger("UI.Twilio.Participant.DisConnected", new Date(), { participant }, room.name);
            }
            setParticipants(prevParticipants => prevParticipants.filter(p => p !== participant));
            setShowWaitingMsg(false);
        };

        room.on('participantConnected', participantConnected);
        room.on('participantDisconnected', participantDisconnected);
        return () => {
            room.off('participantConnected', participantConnected);
            room.off('participantDisconnected', participantDisconnected);
        };
    }, [room, _isMounted]);

    return { participants, showWaitingMsg };
}
